import { useCallback } from 'react';
import { useAsyncData } from './UseAsyncData';

/**
 * id를 가진 엔티티 목록의 fetch/loading/reload/merge 공통 패턴. 뼈대(로딩·재조회·경쟁
 * 응답 무시·실패 토스트)는 `useAsyncData`가 맡고, 여기서는 목록이라서 필요한
 * replaceItem/mergeResults만 얹는다.
 *
 * `useCards`(UseCardData.ts)와 `StockPageContext`가 거의 같은 로직(카드 목록을 불러와
 * loading을 관리하고, 잡 완료 시 결과를 병합)을 각자 손으로 구현하고 있어 여기로 모은다.
 *
 * 재조회 실패 토스트를 끄는 방법은 `useAsyncData`의 `setInlineError`(런타임 토글)와 같다 —
 * 인라인 배너를 그리는 화면이 마운트 동안 `useInlineLoadError(...)` 로 켠다.
 *
 * @param fetchFn 목록을 가져오는 함수 — 매 렌더마다 새로 만들지 않는 안정적인 참조여야
 *                한다(그렇지 않으면 reload가 매번 바뀌어 재조회 루프가 생긴다). `api.ts`에서
 *                가져온 함수를 그대로 넘기면 된다.
 */
export function useEntityList<T extends { id: number }>(
  fetchFn: () => Promise<T[]>,
  errorMessage: string,
) {
  const {
    data: items, setData: setItems, loading, reload, error, retry, hasLoadedOnce, setInlineError, loadVersion,
  } = useAsyncData<T[]>(fetchFn, errorMessage, []);

  const replaceItem = useCallback((item: T) => {
    setItems((prev) => prev.map((c) => (c.id === item.id ? item : c)));
  }, [setItems]);

  // id → 새 값 Map 으로 한 번에(감사 card-controltower#532) — 예전 prev.map 안의 results.find 는 O(목록 × 결과)라
  // 카드 수천 장 × 잡 결과 수천 건이면 수백만 번 비교로 잡 완료 때 화면이 멈췄다. 같은 id 가 두 번이면 예전처럼 앞의 것.
  const mergeResults = useCallback((results: T[]) => {
    if (results.length === 0) return;
    const byId = new Map<number, T>();
    results.forEach((u) => { if (!byId.has(u.id)) byId.set(u.id, u); });
    setItems((prev) => prev.map((c) => byId.get(c.id) ?? c));
  }, [setItems]);

  return {
    items, setItems, loading, reload, error, retry, replaceItem, mergeResults, hasLoadedOnce, setInlineError, loadVersion,
  };
}
